import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const SearchableFarmSelect = ({ farms = [], value, onChange, name = 'farmId', required = false, placeholder = 'Select Farm' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter farms based on search
  const filteredFarms = farms.filter(farm =>
    farm.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get selected farm label
  const getSelectedLabel = () => {
    if (!value) return placeholder;
    const selected = farms.find(f => String(f.id) === String(value));
    return selected ? selected.name : placeholder;
  };

  const handleSelect = (farmId) => {
    onChange(farmId);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Hidden input for form submission */}
      <input type="hidden" name={name} value={value || ''} required={required} />

      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="input w-full text-left cursor-pointer flex items-center justify-between pr-10"
      >
        <span className={`truncate ${!value ? 'text-gray-400' : ''}`}>
          {getSelectedLabel()}
        </span>
        <ChevronDownIcon className={`h-4 w-4 absolute right-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search farms..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-56 overflow-y-auto">
            {filteredFarms.length > 0 ? (
              filteredFarms.map((farm) => (
                <div
                  key={farm.id}
                  onClick={() => handleSelect(farm.id)}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm ${
                    String(value) === String(farm.id) ? 'bg-primary-50 text-primary-600 font-medium' : ''
                  }`}
                >
                  {farm.name}
                </div>
              ))
            ) : (
              <div className="px-4 py-4 text-center text-gray-500 text-sm">
                No farms found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableFarmSelect;
